import {
    IonAlert,
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonLoading,
    IonPage,
    IonTitle,
    IonToast,
    IonToolbar,
    useIonViewDidEnter
} from '@ionic/react';
import { arrowBack, closeCircleOutline, copyOutline, searchOutline, shareOutline } from 'ionicons/icons';
import React, { useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { MessageContext } from '../../../../../contexts/MessageContext';
import {
    fiscalBuscarNFCePorChave,
    fiscalCancelarNFCe,
    fiscalConsultarNFCe,
    requestService
} from '../../../../../Utils/Services';
import './FiscalDetalhes.css';

type Props = {
    abrirMenu: boolean;
};

const FiscalDetalhes: React.FC<Props> = (props: Props) => {
    const history = useHistory();
    const location = useLocation<{ nota?: any }>();
    const { loja } = useContext(MessageContext);
    const { esconderMenu, setEsconderMenu } = useContext(MessageContext);
    const { nomeMenu, setNomeMenu } = useContext(MessageContext);

    const [nota, setNota] = useState<any>(location.state?.nota || null);
    const [showLoading, setShowLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("Carregando...");
    const [showToast, setShowToast] = useState(false);
    const [textToast, setTextToast] = useState("");
    const [toastColor, setToastColor] = useState("success");

    // Certificado
    const [certificadoSenha, setCertificadoSenha] = useState("");

    // Cancelamento
    const [showCancelAlert, setShowCancelAlert] = useState(false);
    const [justificativa, setJustificativa] = useState("");
    const [showJustificativaModal, setShowJustificativaModal] = useState(false);

    useIonViewDidEnter(() => {
        setEsconderMenu(true);
        setNomeMenu("");
        if (nota?.chave) {
            buscarNotaPorChave(nota.chave);
        }
    });

    function buscarNotaPorChave(chave: string) {
        requestService(
            fiscalBuscarNFCePorChave.url.replace("{chave}", chave),
            { method: fiscalBuscarNFCePorChave.method, credentials: 'include' },
            history,
            (response: any) => {
                setNota(response.dados || response);
            },
            (error: any) => {
                console.log("Erro ao buscar nota:", error);
            }
        );
    }

    function consultarSefaz() {
        if (!nota?.chave) return;
        if (!certificadoSenha) {
            mostrarToast("Informe a senha do certificado.", "warning");
            return;
        }

        setShowLoading(true);
        setLoadingMessage("Consultando SEFAZ...");

        requestService(
            fiscalConsultarNFCe.url,
            {
                method: fiscalConsultarNFCe.method,
                body: JSON.stringify({
                    empresaId: loja,
                    certificadoSenha: certificadoSenha,
                    chaveAcesso: nota.chave
                }),
                credentials: 'include',
                timeout: 30000
            },
            history,
            (response: any) => {
                setShowLoading(false);
                const dados = response.dados || response;
                mostrarToast(`Status: ${dados.status} - ${dados.motivo || ""}`, "success");
            },
            (error: any) => {
                setShowLoading(false);
                mostrarToast(error.message || "Erro ao consultar SEFAZ.", "danger");
            }
        );
    }

    function iniciarCancelamento() {
        if (!nota) return;

        // Verificar se a nota pode ser cancelada (até 24h)
        const dataEmissao = new Date(nota.dataEmissao || nota.createdAt);
        const agora = new Date();
        const diffHoras = (agora.getTime() - dataEmissao.getTime()) / (1000 * 60 * 60);

        if (diffHoras > 24) {
            mostrarToast("NFC-e só pode ser cancelada em até 24 horas após emissão.", "warning");
            return;
        }

        setJustificativa("");
        setShowJustificativaModal(true);
    }

    function confirmarCancelamento() {
        if (justificativa.length < 15) {
            mostrarToast("A justificativa deve ter no mínimo 15 caracteres.", "warning");
            return;
        }
        if (!certificadoSenha) {
            mostrarToast("Informe a senha do certificado.", "warning");
            return;
        }

        setShowJustificativaModal(false);
        setShowLoading(true);
        setLoadingMessage("Cancelando NFC-e...");

        requestService(
            fiscalCancelarNFCe.url,
            {
                method: fiscalCancelarNFCe.method,
                body: JSON.stringify({
                    empresaId: loja,
                    certificadoSenha: certificadoSenha,
                    nfceId: nota.id || nota.nfceId,
                    chaveAcesso: nota.chave,
                    protocolo: nota.protocolo,
                    justificativa: justificativa
                }),
                credentials: 'include',
                timeout: 30000
            },
            history,
            (response: any) => {
                setShowLoading(false);
                mostrarToast("NFC-e cancelada com sucesso!", "success");
                setNota({ ...nota, status: "CANCELADA" });
            },
            (error: any) => {
                setShowLoading(false);
                mostrarToast(error.message || "Erro ao cancelar NFC-e.", "danger");
            }
        );
    }

    function copiarChave() {
        if (nota?.chave) {
            navigator.clipboard.writeText(nota.chave).then(() => {
                mostrarToast("Chave copiada!", "success");
            });
        }
    }

    function compartilhar() {
        if (navigator.share && nota) {
            navigator.share({
                title: `NFC-e nº ${nota.numero}`,
                text: `Chave de acesso: ${nota.chave}`,
            }).catch(() => { });
        }
    }

    function formatarValor(valor: number) {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency', currency: 'BRL'
        }).format(valor || 0);
    }

    function formatarData(data: string) {
        if (!data) return "";
        return new Date(data).toLocaleDateString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    }

    function getStatusClass(status: string) {
        if (!status) return "";
        const s = status.toUpperCase();
        if (s.includes("AUTORIZADA")) return "autorizada";
        if (s.includes("CANCELADA")) return "cancelada";
        if (s.includes("INUTILIZADA")) return "inutilizada";
        return "";
    }

    function isAutorizada() {
        return nota?.status?.toUpperCase()?.includes("AUTORIZADA");
    }

    function mostrarToast(msg: string, color: string) {
        setTextToast(msg);
        setToastColor(color);
        setShowToast(true);
    }

    if (!nota) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar color="primary">
                        <IonButtons slot="start">
                            <IonBackButton icon={arrowBack} text='' />
                        </IonButtons>
                        <IonTitle>Detalhes da Nota</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent color="light">
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <p>Nota não encontrada.</p>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar color="primary">
                    <IonButtons slot="start">
                        <IonBackButton icon={arrowBack} text='' />
                    </IonButtons>
                    <IonTitle>NFC-e nº {nota.numero || "—"}</IonTitle>
                </IonToolbar>
            </IonHeader>

            <IonContent className="fiscal-detalhes-container" color="light">

                {/* Status Banner */}
                <div className={`fiscal-detalhes-status-banner ${getStatusClass(nota.status)}`}>
                    <h2>{nota.status || "—"}</h2>
                    <p>{formatarData(nota.dataEmissao || nota.createdAt)}</p>
                </div>

                {/* Dados Gerais */}
                <IonCard className="fiscal-detalhes-card">
                    <IonCardContent>
                        <div className="card-title">Dados da Nota</div>
                        <div className="fiscal-detalhe-row">
                            <span className="fiscal-detalhe-label">Número</span>
                            <span className="fiscal-detalhe-value">{nota.numero || "—"}</span>
                        </div>
                        <div className="fiscal-detalhe-row">
                            <span className="fiscal-detalhe-label">Série</span>
                            <span className="fiscal-detalhe-value">{nota.serie || "1"}</span>
                        </div>
                        <div className="fiscal-detalhe-row">
                            <span className="fiscal-detalhe-label">Protocolo</span>
                            <span className="fiscal-detalhe-value">{nota.protocolo || "—"}</span>
                        </div>
                        <div className="fiscal-detalhe-row">
                            <span className="fiscal-detalhe-label">Valor Total</span>
                            <span className="fiscal-detalhe-value">{formatarValor(nota.valorTotal)}</span>
                        </div>
                    </IonCardContent>
                </IonCard>

                {/* Chave de Acesso */}
                {nota.chave && (
                    <IonCard className="fiscal-detalhes-card">
                        <IonCardContent>
                            <div className="card-title">Chave de Acesso</div>
                            <div className="fiscal-chave-acesso">{nota.chave}</div>
                            <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                                <IonButton expand="block" fill="outline" size="small" onClick={copiarChave} style={{ flex: 1 }}>
                                    <IonIcon slot="start" icon={copyOutline} />
                                    Copiar
                                </IonButton>
                                <IonButton expand="block" fill="outline" size="small" onClick={compartilhar} style={{ flex: 1 }}>
                                    <IonIcon slot="start" icon={shareOutline} />
                                    Compartilhar
                                </IonButton>
                            </div>
                        </IonCardContent>
                    </IonCard>
                )}

                {/* Itens */}
                {nota.itens && nota.itens.length > 0 && (
                    <IonCard className="fiscal-detalhes-card">
                        <IonCardContent>
                            <div className="card-title">Itens</div>
                            <table className="fiscal-itens-table">
                                <thead>
                                    <tr>
                                        <th>Produto</th>
                                        <th className="text-right">Qtd</th>
                                        <th className="text-right">Unit.</th>
                                        <th className="text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {nota.itens.map((item: any, idx: number) => (
                                        <tr key={idx}>
                                            <td>{item.descricao || item.nome}</td>
                                            <td className="text-right">{item.quantidade}</td>
                                            <td className="text-right">{formatarValor(item.valorUnitario)}</td>
                                            <td className="text-right">{formatarValor(item.valorTotal || (item.quantidade * item.valorUnitario))}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </IonCardContent>
                    </IonCard>
                )}

                {/* Pagamentos */}
                {nota.pagamentos && nota.pagamentos.length > 0 && (
                    <IonCard className="fiscal-detalhes-card">
                        <IonCardContent>
                            <div className="card-title">Pagamentos</div>
                            {nota.pagamentos.map((pag: any, idx: number) => (
                                <div className="fiscal-detalhe-row" key={idx}>
                                    <span className="fiscal-detalhe-label">{pag.tipo || pag.descricao}</span>
                                    <span className="fiscal-detalhe-value">{formatarValor(pag.valor)}</span>
                                </div>
                            ))}
                        </IonCardContent>
                    </IonCard>
                )}

                {/* Ações */}
                <IonCard className="fiscal-detalhes-card">
                    <IonCardContent>
                        <div className="card-title">Senha do Certificado</div>
                        <IonItem style={{ marginBottom: "12px" }}>
                            <IonLabel position="stacked">Necessária para consultar ou cancelar</IonLabel>
                            <IonInput
                                type="password"
                                value={certificadoSenha}
                                placeholder="Senha do certificado digital"
                                onIonChange={(e: any) => setCertificadoSenha(e.detail.value || "")}
                            />
                        </IonItem>
                    </IonCardContent>
                </IonCard>

                <div className="fiscal-action-buttons">
                    <IonButton expand="block" fill="outline" onClick={consultarSefaz}>
                        <IonIcon slot="start" icon={searchOutline} />
                        Consultar na SEFAZ
                    </IonButton>

                    {isAutorizada() && (
                        <IonButton expand="block" color="danger" fill="outline" onClick={iniciarCancelamento}>
                            <IonIcon slot="start" icon={closeCircleOutline} />
                            Cancelar NFC-e
                        </IonButton>
                    )}
                </div>

                {/* Modal de justificativa para cancelamento */}
                <IonAlert
                    isOpen={showJustificativaModal}
                    onDidDismiss={() => setShowJustificativaModal(false)}
                    header={`Cancelar NFC-e nº ${nota.numero}`}
                    message="Informe a justificativa (mínimo 15 caracteres):"
                    inputs={[
                        {
                            name: 'justificativa',
                            type: 'textarea',
                            placeholder: 'Motivo do cancelamento...',
                            attributes: { minlength: 15 }
                        }
                    ]}
                    buttons={[
                        { text: 'Voltar', role: 'cancel' },
                        {
                            text: 'Cancelar NFC-e',
                            handler: (data) => {
                                setJustificativa(data.justificativa || "");
                                if ((data.justificativa || "").length < 15) {
                                    mostrarToast("Justificativa deve ter no mínimo 15 caracteres.", "warning");
                                    return false;
                                }
                                setJustificativa(data.justificativa);
                                confirmarCancelamento();
                            }
                        }
                    ]}
                />

                <IonLoading isOpen={showLoading} message={loadingMessage} />

                <IonToast
                    isOpen={showToast}
                    position="top"
                    onDidDismiss={() => setShowToast(false)}
                    message={textToast}
                    duration={3000}
                    color={toastColor}
                />
            </IonContent>
        </IonPage>
    );
};

export default FiscalDetalhes;
